import { QueryHookOptions } from '@apollo/client';
import {
  useApiLazyQuery,
  useApiMutation,
  useApiQuery,
  useApiUploader
} from '@common/hooks';
import { ErrorLogs } from '@common/types';

import {
  fetchedPersonAddressList,
  getBusinessPartnerPersons
} from '../proposal/services';
import {
  calculateAmendmentMutation,
  calculateCancellationAmendmentMutation,
  cancelAmendmentMutation,
  cancelCancellationAmendmentMutation,
  confirmAmendmentCancellationMutation,
  confirmAmendmentMutation,
  getAmendmentCancellationInsuredDetails,
  getAmendmentEndorsementInsuredDetails,
  getAmendmentClauses,
  getAmendmentDetails,
  getAmendmentExpatLovs,
  getAmendmentFilters,
  getAmendmentGroupMedicalPremiumDetails,
  getAmendmentMarineLovs,
  getAmendmentMedicalInsuredsLovs,
  getAmendmentMoreDetails,
  getAmendmentPolicyBusinessPartners,
  getAmendmentPremiumDetails,
  getAmendmentPremiumDetailsTravel,
  getAmendmentsList,
  getAmendmentSubTypes,
  getDestinationsLookup,
  getGeoLocations,
  getInsuredDetailsLovs,
  getMedicalClasses,
  getMedicalLevels,
  getPolicyBusinessPartnerVariance,
  getPolicyMedicalCovers,
  getPolicyTravelCovers,
  getSystemReferenceDateQuery,
  modifyCancellationAmendmentMutation,
  searchAmendments,
  uploadAmendmentMedicalInsuredList
} from './services';
import {
  AmendmentDetailsResponse,
  AmendmentDetailsVariables,
  AmendmentMoreDetailsResponse,
  CalculateAmendmentResponse,
  CalculateAmendmentVariables,
  CalculateCancellationAmendmentResponse,
  CalculateCancellationAmendmentVariables,
  CancelAmendmentResponse,
  CancelAmendmentVariables,
  CancelCancellationAmendmentResponse,
  CancelCancellationAmendmentVariables,
  ConfirmAmendmentCancellationResponse,
  ConfirmAmendmentCancellationVariables,
  ConfirmAmendmentResponse,
  ConfirmAmendmentVariables,
  GetAmendmentCancellationInsuredDetailsResponse,
  GetAmendmentCancellationInsuredDetailsVariables,
  GetAmendmentEndorsementInsuredDetailsResponse,
  GetAmendmentEndorsementInsuredDetailsVariables,
  GetAmendmentClausesResponse,
  GetAmendmentClausesVariables,
  GetAmendmentExpatLovsResponse,
  GetAmendmentFiltersResponse,
  GetAmendmentGroupMedicalPremiumDetailsResponse,
  GetAmendmentGroupMedicalPremiumDetailsVariables,
  GetAmendmentMarineLovsResponse,
  GetAmendmentPolicyBusinessPartnersResponse,
  GetAmendmentPolicyBusinessPartnersVariables,
  GetAmendmentPremiumDetailsResponse,
  GetAmendmentPremiumDetailsTravelResponse,
  GetAmendmentPremiumDetailsTravelVariables,
  GetAmendmentPremiumDetailsVariables,
  GetAmendmentsListResponse,
  GetAmendmentsListVariables,
  GetAmendmentSubTypesResponse,
  GetDestinationsLookupResponse,
  GetGeoLocationsResponse,
  GetInsuredDetailsLovsResponse,
  GetMedicalClassesResponse,
  GetMedicalLevelsResponse,
  GetPolicyAmendmentsResponse,
  GetPolicyAmendmentsVariables,
  GetPolicyMedicalCoversResponse,
  GetPolicyMedicalCoversVariables,
  GetPolicyTravelCoversResponse,
  GetPolicyTravelCoversVariables,
  ModifyAmendmentResponse,
  ModifyAmendmentVariables,
  UploadAmendmentMedicalInsuredListResponse
} from './types';

const useSearchAmendments = (
  options?: QueryHookOptions<
    GetPolicyAmendmentsResponse,
    GetPolicyAmendmentsVariables
  >
) =>
  useApiLazyQuery<GetPolicyAmendmentsResponse, GetPolicyAmendmentsVariables>(
    searchAmendments,
    'searchAmendments',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetAmendmentDetails = (
  options?: QueryHookOptions<
    AmendmentDetailsResponse,
    AmendmentDetailsVariables
  >
) =>
  useApiQuery<AmendmentDetailsResponse, AmendmentDetailsVariables>(
    getAmendmentDetails,
    'getAmendmentDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useGetAmendmentMoreDetails = (
  options?: QueryHookOptions<
    AmendmentMoreDetailsResponse,
    AmendmentDetailsVariables
  >
) =>
  useApiLazyQuery<AmendmentMoreDetailsResponse, AmendmentDetailsVariables>(
    getAmendmentMoreDetails,
    'getAmendmentMoreDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useGetAmendmentSubTypes = (
  options?: QueryHookOptions<GetAmendmentSubTypesResponse>
) =>
  useApiLazyQuery<GetAmendmentSubTypesResponse>(
    getAmendmentSubTypes,
    'getAmendmentSubTypes',
    { ...options, fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true },
    { withLoader: false }
  );

const useGetBusinessPartnerPersons = () =>
  useApiLazyQuery<
    any,
    { mainbusinessPartnerID: string; searchKeyword: string }
  >(
    getBusinessPartnerPersons,
    'getBusinessPartnerPersons',
    { fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true },
    { withLoader: false }
  );

const useGetPersonAddressList = () =>
  useApiLazyQuery<any, { selectedPersonID: string }>(
    fetchedPersonAddressList,
    'getPersonAddressList',
    { fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true },
    { withLoader: false }
  );

export const useGetAmendmentMarineLovs = () =>
  useApiQuery<GetAmendmentMarineLovsResponse>(
    getAmendmentMarineLovs,
    'getAmendmentMarineLovs',
    { fetchPolicy: 'no-cache' }
  );

export const useGetAmendmentExpatLovs = () =>
  useApiQuery<GetAmendmentExpatLovsResponse>(
    getAmendmentExpatLovs,
    'getAmendmentExpatLovs',
    { fetchPolicy: 'no-cache' }
  );

export const useGetDestinationsLookup = () =>
  useApiQuery<GetDestinationsLookupResponse>(
    getDestinationsLookup,
    'getDestinationsLookup',
    { fetchPolicy: 'no-cache' }
  );

export const useGetAmendmentMedicalInsuredsLovs = (
  options?: QueryHookOptions<any>
) =>
  useApiQuery<any>(
    getAmendmentMedicalInsuredsLovs,
    'getAmendmentMedicalInsuredsLovs',
    { fetchPolicy: 'no-cache', ...options }
  );

/** LOV: GeoLocations */
const useGetGeoLocations = (
  options?: QueryHookOptions<GetGeoLocationsResponse>
) =>
  useApiQuery<GetGeoLocationsResponse>(
    getGeoLocations,
    'getGeoLocations',
    { ...options, fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true },
    { withLoader: false }
  );

const useCancelAmendment = (
  options?: QueryHookOptions<CancelAmendmentResponse, CancelAmendmentVariables>
) =>
  useApiMutation<CancelAmendmentResponse, CancelAmendmentVariables>(
    cancelAmendmentMutation,
    'cancelAmendment',
    {
      withLoader: true,
      successMsg: 'Amendment cancelled successfully'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getAmendmentDetails]
    }
  );

const useConfirmAmendment = (
  options?: QueryHookOptions<
    ConfirmAmendmentResponse,
    ConfirmAmendmentVariables
  >
) =>
  useApiMutation<ConfirmAmendmentResponse, ConfirmAmendmentVariables>(
    confirmAmendmentMutation,
    'confirmAmendment',
    {
      withLoader: true,
      successMsg: 'Amendment confirmed successfully'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getAmendmentDetails]
    }
  );

const useConfirmAmendmentCancellation = (
  options?: QueryHookOptions<
    ConfirmAmendmentCancellationResponse,
    ConfirmAmendmentCancellationVariables
  >
) =>
  useApiMutation<
    ConfirmAmendmentCancellationResponse,
    ConfirmAmendmentCancellationVariables
  >(
    confirmAmendmentCancellationMutation,
    'confirmAmendmentCancellationMutation',
    {
      withLoader: true,
      successMsg: 'Amendment confirmed successfully'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getAmendmentDetails]
    }
  );

const useGetAmendmentClauses = (
  options?: QueryHookOptions<
    GetAmendmentClausesResponse,
    GetAmendmentClausesVariables
  >
) =>
  useApiQuery<GetAmendmentClausesResponse, GetAmendmentClausesVariables>(
    getAmendmentClauses,
    'getAmendmentClauses',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useCalculateAmendment = (
  options?: QueryHookOptions<
    CalculateAmendmentResponse,
    CalculateAmendmentVariables
  >
) =>
  useApiMutation<CalculateAmendmentResponse, CalculateAmendmentVariables>(
    calculateAmendmentMutation,
    'calculateAmendment',
    {
      withLoader: false,
      successMsg: 'Amendment calculated successfully'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getAmendmentDetails]
    }
  );

const useCalculateAmendmentCancellation = (
  options?: QueryHookOptions<
    CalculateCancellationAmendmentResponse,
    CalculateCancellationAmendmentVariables
  >
) =>
  useApiMutation<
    CalculateCancellationAmendmentResponse,
    CalculateCancellationAmendmentVariables
  >(
    calculateCancellationAmendmentMutation,
    'calculateCancellationAmendment',
    {
      withLoader: false,
      successMsg: 'Amendment Cancellation calculated successfully'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getAmendmentDetails]
    }
  );

const useGetSystemReferenceDate = () =>
  useApiQuery<{ core: { getSystemReferenceDate: string } }>(
    getSystemReferenceDateQuery,
    'getSystemReferenceDate',
    { fetchPolicy: 'network-only' },
    { withLoader: false }
  );

const useGetAmendments = (
  options?: QueryHookOptions<
    GetAmendmentsListResponse,
    GetAmendmentsListVariables
  >
) =>
  useApiQuery<GetAmendmentsListResponse, GetAmendmentsListVariables>(
    getAmendmentsList,
    'getAmendments',
    {
      ...options,
      fetchPolicy: 'no-cache'
    },
    {
      withLoader: false
    }
  );

const useGetAmendmentFilters = () =>
  useApiQuery<GetAmendmentFiltersResponse>(
    getAmendmentFilters,
    'getAmendmentFilters',
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true
    }
  );

const useGetInsuredDetailsLovs = () =>
  useApiQuery<GetInsuredDetailsLovsResponse>(
    getInsuredDetailsLovs,
    'getInsuredDetailsLovs',
    { fetchPolicy: 'no-cache' }
  );

const useGetMedicalClasses = () =>
  useApiQuery<GetMedicalClassesResponse>(
    getMedicalClasses,
    'getMedicalClasses',
    { fetchPolicy: 'no-cache' }
  );

const useGetMedicalLevels = () =>
  useApiQuery<GetMedicalLevelsResponse>(getMedicalLevels, 'getMedicalLevels', {
    fetchPolicy: 'no-cache'
  });

const useGetPolicyMedicalCovers = (
  options?: QueryHookOptions<
    GetPolicyMedicalCoversResponse,
    GetPolicyMedicalCoversVariables
  >
) =>
  useApiQuery<GetPolicyMedicalCoversResponse, GetPolicyMedicalCoversVariables>(
    getPolicyMedicalCovers,
    'getPolicyMedicalCovers',
    {
      ...options,
      fetchPolicy: 'no-cache'
    },
    { withLoader: false }
  );

const useGetPolicyTravelCovers = (
  options?: QueryHookOptions<
    GetPolicyTravelCoversResponse,
    GetPolicyTravelCoversVariables
  >
) =>
  useApiQuery<GetPolicyTravelCoversResponse, GetPolicyTravelCoversVariables>(
    getPolicyTravelCovers,
    'getPolicyTravelCovers',
    {
      ...options,
      fetchPolicy: 'no-cache'
    },
    { withLoader: false }
  );

const useGetAmendmentPolicyBusinessPartners = () =>
  useApiLazyQuery<
    GetAmendmentPolicyBusinessPartnersResponse,
    GetAmendmentPolicyBusinessPartnersVariables
  >(
    getAmendmentPolicyBusinessPartners,
    'getAmendmentPolicyBusinessPartners',
    { fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true },
    { withLoader: false }
  );

const useGetPolicyBusinessPartnersVariance = () =>
  useApiLazyQuery<
    GetAmendmentPolicyBusinessPartnersResponse,
    GetAmendmentPolicyBusinessPartnersVariables
  >(
    getPolicyBusinessPartnerVariance,
    'getPolicyBusinessPartnerVariance',
    { fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true },
    { withLoader: false }
  );

const useGetAmendmentPremiumDetailsQuery = (
  options?: QueryHookOptions<
    GetAmendmentPremiumDetailsResponse,
    GetAmendmentPremiumDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetAmendmentPremiumDetailsResponse,
    GetAmendmentPremiumDetailsVariables
  >(
    getAmendmentPremiumDetails,
    'getAmendmentPremiumDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetAmendmentGroupMedicalPremiumDetailsQuery = (
  options?: QueryHookOptions<
    GetAmendmentGroupMedicalPremiumDetailsResponse,
    GetAmendmentGroupMedicalPremiumDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetAmendmentGroupMedicalPremiumDetailsResponse,
    GetAmendmentGroupMedicalPremiumDetailsVariables
  >(
    getAmendmentGroupMedicalPremiumDetails,
    'getAmendmentGroupMedicalPremiumDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetAmendmentPremiumDetailsTravelQuery = (
  options?: QueryHookOptions<
    GetAmendmentPremiumDetailsTravelResponse,
    GetAmendmentPremiumDetailsTravelVariables
  >
) =>
  useApiLazyQuery<
    GetAmendmentPremiumDetailsTravelResponse,
    GetAmendmentPremiumDetailsTravelVariables
  >(
    getAmendmentPremiumDetailsTravel,
    'getAmendmentPremiumDetailsTravel',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetAmendmentCancellationInsuredDetailsQuery = (
  options?: QueryHookOptions<
    GetAmendmentCancellationInsuredDetailsResponse,
    GetAmendmentCancellationInsuredDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetAmendmentCancellationInsuredDetailsResponse,
    GetAmendmentCancellationInsuredDetailsVariables
  >(
    getAmendmentCancellationInsuredDetails,
    'getAmendmentCancellationInsuredDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useGetAmendmentEndorsementInsuredDetailsQuery = (
  options?: QueryHookOptions<
    GetAmendmentEndorsementInsuredDetailsResponse,
    GetAmendmentEndorsementInsuredDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetAmendmentEndorsementInsuredDetailsResponse,
    GetAmendmentEndorsementInsuredDetailsVariables
  >(
    getAmendmentEndorsementInsuredDetails,
    'getAmendmentEndorsementInsuredDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useModifyCancellationAmendment = (
  options?: QueryHookOptions<ModifyAmendmentResponse, ModifyAmendmentVariables>
) =>
  useApiMutation<ModifyAmendmentResponse, ModifyAmendmentVariables>(
    modifyCancellationAmendmentMutation,
    'modifyCancellationAmendment',
    {
      withLoader: false,
      successMsg: 'Amendment modified successfully'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getAmendmentDetails]
    }
  );

const useCancelCancellationAmendment = (
  options?: QueryHookOptions<
    CancelCancellationAmendmentResponse,
    CancelCancellationAmendmentVariables
  >
) =>
  useApiMutation<
    CancelCancellationAmendmentResponse,
    CancelCancellationAmendmentVariables
  >(
    cancelCancellationAmendmentMutation,
    'cancelCancellationAmendment',
    {
      withLoader: true,
      successMsg: 'Amendment cancelled successfully!'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getAmendmentDetails]
    }
  );

const useUploadAmendmentMedicalInsuredList = (
  amendmentId: string,
  setPopUpOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setErrorsLogs: React.Dispatch<React.SetStateAction<ErrorLogs[]>>
) => {
  const apiUploader = useApiUploader<
    UploadAmendmentMedicalInsuredListResponse,
    { amendmentId: string }
  >();

  return async (file: File) =>
    apiUploader(uploadAmendmentMedicalInsuredList, { amendmentId }, file, {
      key: 'amendmentGroupMedicalUpload',
      successMessage: 'File uploaded successfully!',
      successCondition: (body) =>
        body.data.production.uploadAmendmentMedicalInsuredList.length === 0,
      onErrorCallback: (body) => {
        if (body.data) {
          setErrorsLogs(body.data.production.uploadAmendmentMedicalInsuredList);
          setPopUpOpen(true);
        }
      }
    });
};

export {
  useCalculateAmendment,
  useCalculateAmendmentCancellation,
  useCancelAmendment,
  useCancelCancellationAmendment,
  useConfirmAmendment,
  useConfirmAmendmentCancellation,
  useGetAmendmentCancellationInsuredDetailsQuery,
  useGetAmendmentEndorsementInsuredDetailsQuery,
  useGetAmendmentClauses,
  useGetAmendmentDetails,
  useGetAmendmentFilters,
  useGetAmendmentGroupMedicalPremiumDetailsQuery,
  useGetAmendmentMoreDetails,
  useGetAmendmentPolicyBusinessPartners,
  useGetAmendmentPremiumDetailsQuery,
  useGetAmendmentPremiumDetailsTravelQuery,
  useGetAmendments,
  useGetAmendmentSubTypes,
  useGetBusinessPartnerPersons,
  useGetGeoLocations,
  useGetInsuredDetailsLovs,
  useGetMedicalClasses,
  useGetMedicalLevels,
  useGetPersonAddressList,
  useGetPolicyBusinessPartnersVariance,
  useGetPolicyMedicalCovers,
  useGetPolicyTravelCovers,
  useGetSystemReferenceDate,
  useModifyCancellationAmendment,
  useSearchAmendments,
  useUploadAmendmentMedicalInsuredList
};
